// This class must provide at least all the methods available on CharStats.

import { debugValidateProps } from "../util/generic";

export class McStats {
    object: any;
    str: number | undefined;
    dex: number | undefined;
    int: number | undefined;
    con: number | undefined;
    cha: number | undefined;
    sen: number | undefined;

    constructor(mcObj: any) {
        this.object = mcObj;

        this.str = undefined;
        this.dex = undefined;
        this.int = undefined;
        this.con = undefined;
        this.cha = undefined;
        this.sen = undefined;
    }

    debugValidate() {
        debugValidateProps(this, [
            ["str", 0, 5000],
            ["dex", 0, 5000],
            ["int", 0, 5000],
            ["con", 0, 5000],
            ["cha", 0, 5000],
            ["sen", 0, 5000],
        ]);
    }

    _getEquipData(equipIdx: any) {
        const itemData = GDM.getNow("item_data");
        const item = this.object.inventory.findByLocSlot(
            ITEMLOC.EQUIPPED_EQUIP,
            equipIdx
        );
        if (!item) {
            // Empty Slot
            return null;
        }
        const equipItemType = ITMPARTTOTYPE[equipIdx];
        if (item.itemType !== equipItemType) {
            console.warn("Item found in incorret equipment slot.");
            return null;
        }
        return itemData.getData(item.itemType, item.itemNo);
    }

    getMaxHp() {
        let iA = 10,
            iM1 = 8,
            fC = 2.25;
        const job = this.object.job;
        const level = this.object.level;

        switch (job) {
            case 111:
                iA = 15;
                fC = 3.5;
                iM1 = 12;
                break;
            case 121:
                iA = 20;
                fC = 5.0;
                iM1 = 20;
                break;
            case 122:
                iA = 15;
                fC = 4.0;
                iM1 = 18;
                break;

            case 211:
                iA = 8;
                fC = 3.25;
                iM1 = 10;
                break;
            case 221:
                iA = 8;
                fC = 3.5;
                iM1 = 15;
                break;
            case 222:
                iA = 8;
                fC = 3.75;
                iM1 = 17;
                break;

            case 311:
                iA = 10;
                fC = 3.0;
                iM1 = 10;
                break;
            case 321:
                iA = 15;
                fC = 3.25;
                iM1 = 18;
                break;
            case 322:
                iA = 12;
                fC = 3.0;
                iM1 = 16;
                break;

            case 411:
                iA = 12;
                fC = 3.0;
                iM1 = 10;
                break;
            case 421:
                iA = 13;
                fC = 4.0;
                iM1 = 19;
                break;
            case 422:
                iA = 15;
                fC = 3.75;
                iM1 = 16;
                break;

            case 411:
                iA = 12;
                fC = 3.0;
                iM1 = 10;
                break;
            case 421:
                iA = 13;
                fC = 4.0;
                iM1 = 19;
                break;
            case 422:
                iA = 15;
                fC = 3.75;
                iM1 = 17;
                break;

            default:
                iA = 10;
                fC = 2.25;
                iM1 = 8;
                break;
        }

        return Math.floor(
            (level + iA) * Math.sqrt(level + iM1) * fC + this.str! * fC
        );
    }

    getMaxMp() {
        let iA = 5,
            fM1 = 3.0,
            fM2 = 2.0;
        const job = this.object.job;
        const level = this.object.level;

        switch (job) {
            case 111:
                iA = 10;
                fM1 = 3.0;
                fM2 = 2.5;
                break;
            case 121:
                iA = 10;
                fM1 = 3.5;
                fM2 = 3.25;
                break;
            case 122:
                iA = 10;
                fM1 = 4.0;
                fM2 = 2.75;
                break;

            case 211:
                iA = 20;
                fM1 = 8.0;
                fM2 = 4.0;
                break;
            case 221:
                iA = 20;
                fM1 = 10.0;
                fM2 = 5.0;
                break;
            case 222:
                iA = 20;
                fM1 = 9.0;
                fM2 = 5.0;
                break;

            case 311:
                iA = 15;
                fM1 = 4.0;
                fM2 = 2.5;
                break;
            case 321:
                iA = 15;
                fM1 = 5.0;
                fM2 = 2.75;
                break;
            case 322:
                iA = 15;
                fM1 = 6.0;
                fM2 = 3.25;
                break;

            case 411:
                iA = 12;
                fM1 = 5.0;
                fM2 = 2.75;
                break;
            case 421:
                iA = 12;
                fM1 = 6.0;
                fM2 = 3.25;
                break;
            case 422:
                iA = 12;
                fM1 = 6.0;
                fM2 = 3.75;
                break;

            default:
                iA = 5;
                fM1 = 3.0;
                fM2 = 2.0;
                break;
        }

        return Math.floor((level + iA) * fM1 + this.int! * fM2);
    }

    getCritical() {
        const level = this.object.level;
        const baseValue = 190 + level * 10;

        return Math.floor(this.sen! + this.con! * 0.2);
    }

    getAttackSpeed() {
        return this.object.calculatedStats?.attackSpeed || 100;
    }

    getAttackCooldownMs() {
        // Keep the local swing cadence in step with Battle.calculateAttackSpeed.
        const dexModifier = 1 + (this.dex ?? 0) * 0.01;
        return Math.max(500, Math.floor(2000 / (dexModifier * this.getAttackSpeed() / 100)));
    }

    getAttackDistance() {
        const weaponData = this._getEquipData(INVEQUIPIDX.WEAPON);
        if (!weaponData) {
            return global.DEFAULT_ATTACK_DISTANCE;
        }
        return weaponData[WEAPON_DATA.RANGE] / 100;
    }

    getAttackPower() {
        // Basic attack power: str * 2 + level + weapon attack
        let attack = this.str! * 2 + this.object.level;
        const weaponData = this._getEquipData(INVEQUIPIDX.WEAPON);
        if (weaponData) {
            attack += weaponData[WEAPON_DATA.ATTACK_POWER] || 0;
        }
        return attack;
    }

    getDefense() {
        // Basic defense: con + equipment defense
        let defense = this.con!;
        // Add defense from armor pieces
        const armorSlots = [
            INVEQUIPIDX.HELMET,
            INVEQUIPIDX.ARMOR,
            INVEQUIPIDX.GAUNTLET,
            INVEQUIPIDX.BOOTS,
        ];
        for (const slot of armorSlots) {
            const equipData = this._getEquipData(slot);
            if (equipData) {
                defense += equipData[10] || 0; // Assuming defense is at index 10
            }
        }
        return defense;
    }

    getMoveSpeed() {
        return 535 + this.dex!;
    }

    getMagicDefense() {
        // TODO: Implement proper magic defense calculation
        return 10; // Stub
    }

    getAccury() {
        // TODO: Implement proper accuracy calculation
        return 30; // Stub
    }

    getDodge() {
        // TODO: Implement proper dodge calculation
        return 30; // Stub
    }

    getMaxWeight() {
        // TODO: Implement proper max weight calculation
        return 1000; // Stub
    }

    getMaxSummonGauge() {
        // TODO: Implement proper max summon gauge calculation
        return 50; // Stub
    }

    getMPReduction() {
        // TODO: Implement proper MP reduction calculation
        return 0; // Stub
    }

    getNeedRawExp() {
        const level = this.object.level;
        if (level <= 15) {
            return Math.floor((level + 3) * (level + 5) * (level + 10) * 0.7);
        } else if (level <= 50) {
            return Math.floor((level - 5) * (level + 2) * (level + 2) * 2.2);
        } else if (level <= 100) {
            return Math.floor((level - 5) * (level + 2) * (level - 38) * 9.0);
        } else if (level <= 139) {
            return Math.floor(
                (level + 27) * (level + 34) * (level + 220) * 1.0
            );
        } else {
            return Math.floor(
                (level - 15) * (level + 7) * (level - 126) * 41.0
            );
        }
    }
}
